import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";

// Distinct chime recipes per index — synthesized, no audio files needed.
const CHIMES = {
  NIFTY: [{ f: 880, t: 0, d: 0.18 }, { f: 1318.5, t: 0.2, d: 0.3 }],                       // ascending two-tone
  BANKNIFTY: [{ f: 660, t: 0, d: 0.12 }, { f: 660, t: 0.18, d: 0.12 }, { f: 660, t: 0.36, d: 0.22 }], // triple beep
  SENSEX: [{ f: 523.25, t: 0, d: 0.5 }, { f: 784, t: 0.05, d: 0.55 }],                     // bell harmony
};
const WATCHED = Object.keys(CHIMES);
const FRESH_MS = 30 * 60 * 1000;

let audioCtx = null;
const ensureCtx = () => {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

export const playChime = (symbol) => {
  const recipe = CHIMES[symbol];
  if (!recipe) return;
  playRecipe(recipe);
};

// Exit chimes: profit = bright ascending triad, loss = low descending two-tone, token = urgent triple.
const EXIT_RECIPES = {
  win: [{ f: 659.25, t: 0, d: 0.14 }, { f: 830.6, t: 0.14, d: 0.14 }, { f: 1046.5, t: 0.28, d: 0.4 }],
  loss: [{ f: 440, t: 0, d: 0.25 }, { f: 311.1, t: 0.28, d: 0.45 }],
  token: [{ f: 987.8, t: 0, d: 0.1 }, { f: 987.8, t: 0.15, d: 0.1 }, { f: 1318.5, t: 0.3, d: 0.35 }],
  theta_signal: [{ f: 740, t: 0, d: 0.15 }, { f: 988, t: 0.17, d: 0.3 }],
  theta_buy: [{ f: 659.25, t: 0, d: 0.12 }, { f: 880, t: 0.14, d: 0.12 }, { f: 1174.7, t: 0.28, d: 0.12 }, { f: 1318.5, t: 0.42, d: 0.45 }],
};
export const playAlertChime = (kind) => playRecipe(EXIT_RECIPES[kind] || EXIT_RECIPES.win);
export const chimesEnabled = () => localStorage.getItem("tiq_chimes") !== "off";

function playRecipe(recipe) {
  try {
    const ctx = ensureCtx();
    const now = ctx.currentTime;
    recipe.forEach(({ f, t, d }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.25, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + d + 0.05);
    });
  } catch (e) { console.warn("chime failed:", e?.message || e); }
}

/** Polls top setups and plays a distinct chime when a fresh NIFTY / SENSEX /
 *  BANKNIFTY signal appears. Bell toggle persists in localStorage. */
export default function SignalChimes() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem("tiq_chimes") !== "off");
  const seenRef = useRef(JSON.parse(localStorage.getItem("tiq_chime_seen") || "{}"));
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => { localStorage.setItem("tiq_chimes", enabled ? "on" : "off"); }, [enabled]);

  // Browsers block audio until a user gesture — unlock the context on first click.
  useEffect(() => {
    const unlock = () => { try { ensureCtx(); } catch {} };
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get("/watchlist/top-setups?limit=10&segment=indian");
        (Array.isArray(data) ? data : []).forEach((s) => {
          const sym = (s.symbol || "").toUpperCase();
          // Chime ONLY for a newly opened TRACKED trade — never for a cache re-scan of the same idea.
          if (!WATCHED.includes(sym) || !s.entry_price || !s.signal_id || !s.signal_opened_at) return;
          const openedMs = Date.parse(s.signal_opened_at);
          if (Number.isNaN(openedMs) || Date.now() - openedMs > FRESH_MS) return;
          if (seenRef.current[sym] === s.signal_id) return;
          seenRef.current[sym] = s.signal_id;
          localStorage.setItem("tiq_chime_seen", JSON.stringify(seenRef.current));
          if (enabledRef.current) {
            playChime(sym);
            toast.info(`${sym} signal spotted`, {
              description: `${(s.trend || "").toUpperCase()} · entry ${s.entry_price} · SL ${s.stop_loss}`,
            });
          }
        });
      } catch (e) { console.warn("chime poll failed:", e?.message || e); }
    };
    poll();
    const id = setInterval(poll, 60000);
    return () => clearInterval(id);
  }, []);

  return (
    <button
      onClick={() => { setEnabled((v) => { const nv = !v; if (nv) playChime("NIFTY"); return nv; }); }}
      title={enabled ? "Signal chimes ON (NIFTY / SENSEX / BANKNIFTY) — click to mute" : "Signal chimes OFF — click to enable"}
      className={`p-1.5 border transition-colors ${enabled ? "border-[#007AFF]/50 text-[#3B9BFF] hover:bg-[#007AFF]/10" : "border-zinc-800 text-zinc-600 hover:text-zinc-400"}`}
      data-testid="signal-chimes-toggle"
    >
      {enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
    </button>
  );
}
